import React, { useMemo } from 'react';
import { useTranslation } from '@app/core/i18n';

import { Dialog, DialogButtonType } from '@ast/magma/components/dialog';

import { uniqueId } from '@ast/magma/inner/utils/uniqueid';

import { FocusBackWrapper } from '@app/common/components/controls/FocusBackWrapper/FocusBackWrapper';
import { DialogWrapper } from '@app/common/components/dialogwrapper';
import { Portal } from '@app/common/components/Portal';
import {
  UserSelectorForm,
} from '@app/common/components/UserSelectorDialog/UserSelectorForm/UserSelectorForm';
import { UserOption } from '@app/common/types/UserOption';
import { useIsMobile } from '@app/common/utils/hooks/useIsMobile';

import styles from './UserSelectorDialog.pcss';

export interface SelectorDialogProps<User extends UserOption> {
  readonly visible: boolean;
  readonly onClose: (selectedUser?: User) => void;
  // eslint-disable-next-line i18next/no-literal-string
  readonly 'data-stable-name': string;
  readonly userOptions: User[];
  readonly currentUser: User;
}
export const UserSelectorDialog = <User extends UserOption>({
  visible,
  onClose,
  // eslint-disable-next-line i18next/no-literal-string
  'data-stable-name': dataStableName,
  userOptions,
  currentUser,
}: SelectorDialogProps<User>) => {
  const { t } = useTranslation();
  const formId = useMemo(() => uniqueId('user-selector-form-id'), []);
  const isMobile = useIsMobile();
  const buttonCancel = {
    label: t(
      'user-selector-dialog.cancel|Cancel button label in "Choose organization" dialog',
      'Cancel',
    ),
    type: DialogButtonType.Close,
    // eslint-disable-next-line i18next/no-literal-string
    'data-stable-name': `${dataStableName}Cancel`,
  };
  const buttonSubmit = {
    label: t(
      'user-selector-dialog.confirm|Confirm button label in "Choose organization" dialog',
      'Confirm',
    ),
    type: DialogButtonType.Submit,
    // eslint-disable-next-line i18next/no-literal-string
    'data-stable-name': `${dataStableName}Submit`,
  };

  return (
    <Portal>
      <DialogWrapper visible={visible} onRecover={() => onClose()}>
        <FocusBackWrapper>
          <Dialog
            title={t(
              'user-selector-dialog.title|Title for "Choose organization" dialog',
              'Choose an organization to manage',
            )}
            fixedTitle
            showClose={{
              // eslint-disable-next-line i18next/no-literal-string
              'aria-label': t(
                'user-selector-dialog.close-button.accessibility|Close button accessibility label in "Choose organization" dialog',
                'Close',
              ),
            }}
            align={isMobile ? 'bottom' : 'right'}
            predefinedWidth="aside"
            form={formId}
            defaultButton={DialogButtonType.Submit}
            focusDefaultButton={false}
            buttons={[buttonCancel, buttonSubmit]}
            onClose={() => onClose()}
            className={styles.userSelectorDialog}
            // disable dialog description to avoid content duplicate voicing
            aria-describedby={undefined}
            data-stable-name={dataStableName}
          >
            <div className={styles.dialogContent}>
              <UserSelectorForm
                data-stable-name="UserSelectorForm"
                userOptions={userOptions}
                currentUser={currentUser}
                onSubmit={(selectedUser: User) => onClose(selectedUser)}
                formId={formId}
              />
            </div>
          </Dialog>
        </FocusBackWrapper>
      </DialogWrapper>
    </Portal>
  );
};
